/**
 * UK region pack — default at v1 launch.
 *
 * Terminology, governance, age-phase labels, and units are aligned with
 * The FA learning ecosystem and Premier League / EPPP guidance. Phase
 * model: Foundation Phase (U9–U11), Youth Development Phase (U12–U16),
 * Professional Development Phase (U17–U21).
 *
 * Phase emphases sourced from the FA's six-core-capabilities curriculum
 * + Premier League academy hours-by-phase guidance: Foundation skews
 * technical and social; YDP balances all four with growth-and-
 * maturation overlays; PDP shifts toward psychological and tactical
 * pressure. Every consumer should treat these as starting weights, not
 * targets — a coach can override per session.
 */
import type { RegionPack } from './types';

export const UK_REGION: RegionPack = {
  code: 'uk',
  label: 'United Kingdom',
  terms: {
    sport: 'football',
    field: 'pitch',
    roster: 'squad',
    game: 'match',
    uniform: 'kit',
    trainingWeek: 'micro-cycle',
    cleats: 'boots',
    governingBody: 'The FA',
    academyStandard: 'EPPP',
    safeguardingLead: 'Welfare Officer',
    safetyOrg: 'FA Safeguarding',
    privacyLaw: 'UK-GDPR + DPA 2018',
    backgroundCheck: 'FA DBS',
    adultLeague: 'first-team / senior football',
  },
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  units: {
    distance: 'metres',
    height: 'cm',
    weight: 'kg',
    temperature: 'celsius',
  },
  privacyMinAge: 13,
  phases: [
    {
      id: 'foundation',
      label: 'Foundation Phase',
      ageRange: [9, 11],
      description: 'U9–U11. Technical mastery + fun + relationships. Bio-banding entry; small-sided games dominate.',
      cornerEmphasis: {
        technical_tactical: 0.35,
        physical: 0.15,
        psychological: 0.20,
        social: 0.30,
      },
    },
    {
      id: 'youth_development',
      label: 'Youth Development Phase',
      ageRange: [12, 16],
      description: 'U12–U16. Game intelligence, growth-and-maturation overlay, position role exposure. Six core capabilities front-of-mind.',
      cornerEmphasis: {
        technical_tactical: 0.32,
        physical: 0.23,
        psychological: 0.23,
        social: 0.22,
      },
    },
    {
      id: 'professional_development',
      label: 'Professional Development Phase',
      ageRange: [17, 21],
      description: 'U17–U21. Performance, dual-career, transition to senior football. Welfare + mental-health infrastructure expected.',
      cornerEmphasis: {
        technical_tactical: 0.30,
        physical: 0.25,
        psychological: 0.25,
        social: 0.20,
      },
    },
  ],
};
